import { useEffect, useState, type ReactNode } from 'react';
import { Dialog, DialogContent } from './Dialog';
import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from './Button';

export interface LightboxImage {
  src: string;
  alt: string;
  caption?: string;
}

interface LightboxProps {
  images: LightboxImage[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialIndex?: number;
}

/**
 * Accessible image lightbox. Traps focus, supports arrow keys, Esc to close,
 * and wraps around when navigating past the ends.
 */
export function Lightbox({ images, open, onOpenChange, initialIndex = 0 }: LightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length);
      else if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, images.length]);

  if (images.length === 0) return null;
  const current = images[index];
  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[100vw] w-full h-[100vh] sm:max-w-[95vw] sm:h-[95vh] border-0 bg-transparent p-0 shadow-none flex items-center justify-center"
        aria-label="Image viewer"
      >
        <div className="relative h-full w-full">
          <img
            src={current.src}
            alt={current.alt}
            className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
          />
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 text-fg hover:bg-bg-elevated/40"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 text-fg hover:bg-bg-elevated/40"
                onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-fg hover:bg-bg-elevated/40"
                onClick={() => setIndex((i) => (i + 1) % images.length)}
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-bg/70 px-3 py-1 text-xs text-fg-muted backdrop-blur-sm">
                {index + 1} / {images.length}
              </div>
            </>
          )}
          {current.caption && (
            <figcaption className="absolute bottom-12 left-1/2 -translate-x-1/2 max-w-2xl rounded-md bg-bg/80 px-4 py-2 text-center text-sm text-fg-muted backdrop-blur-sm">
              {current.caption}
            </figcaption>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface GalleryGridProps {
  images: LightboxImage[];
  className?: string;
  /** Optional custom rendering of the trigger element. */
  renderTrigger?: (img: LightboxImage, index: number, onClick: () => void) => ReactNode;
}

/**
 * Mixed-grid gallery (1 hero + thumbs) wired to the lightbox.
 */
export function GalleryGrid({ images, className, renderTrigger }: GalleryGridProps) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  if (images.length === 0) return null;

  const openAt = (idx: number) => {
    setActiveIdx(idx);
    setOpen(true);
  };

  const defaultTrigger = (img: LightboxImage, idx: number, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="group relative overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
      aria-label={`Open image ${idx + 1}: ${img.alt}`}
    >
      <img
        src={img.src}
        alt={img.alt}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-cover transition-transform duration-slow group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-bg/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );

  const trigger = renderTrigger ?? defaultTrigger;

  return (
    <>
      <div
        className={cn(
          'grid gap-3',
          images.length === 1 && 'grid-cols-1',
          images.length === 2 && 'grid-cols-2',
          images.length >= 3 && 'grid-cols-2 md:grid-cols-4',
          images.length >= 3 && 'auto-rows-[180px] md:auto-rows-[200px]',
          className,
        )}
      >
        {images.map((img, idx) => {
          // First image is hero spanning 2 cols / 2 rows
          const isHero = idx === 0 && images.length >= 3;
          return (
            <div
              key={img.src}
              className={cn(isHero && 'col-span-2 row-span-2')}
            >
              {trigger(img, idx, () => openAt(idx))}
            </div>
          );
        })}
      </div>
      <Lightbox
        images={images}
        open={open}
        onOpenChange={setOpen}
        initialIndex={activeIdx}
      />
    </>
  );
}